var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import PropTypes from 'prop-types';
import * as React from 'react';
import styles from './RadioList.css';
import cx from 'classnames';
import genUniqueId from 'utils/uniqueId';
import { get } from 'lodash';
function Radio(props) {
    const { id, name, checked = false, children, style, required, disabled = false, hideRadio, allowUnselect } = props;
    const idStr = id.toString();
    const { onChange } = props;
    const doesChildHaveHideRadio = Boolean(get(children, 'props.hideRadio'));
    const childHideRadio = hideRadio || doesChildHaveHideRadio;
    // If disabled is an object-like item, convert that to a boolean
    const isDisabled = Boolean(disabled);
    // We use a click handler on the enclosing <label> instead of an onChange
    // handler on the <input> tag for one reason: onChange on <input> doesn't
    // get triggered in Firefox if the click happens on an element inside the
    // label that in turn has a click handler.
    //
    // However, this function would get called twice if the click happens on a
    // non-clickable area since the radio element's event gets propagated up (my
    // understanding). To avoid this from happening, we need the calls to stop
    // propagation of the events. We also don't need the default action since we
    // set the `checked` flag manually. Since the controlled input component
    // doesn't have an onChange function but is controlled manually, we need to
    // make it `readOnly`.
    const onClickWrapper = evt => {
        evt.stopPropagation();
        evt.preventDefault();
        if (isDisabled)
            return;
        // If `allowUnselect` prop not enabled, and if the current id is already
        // checked, avoid triggerring the change event.
        if (allowUnselect && checked) {
            onChange('');
        }
        else if (!checked) {
            onChange(id);
        }
    };
    const className = cx(styles['radio-label'], props.className, {
        [styles['radio-disabled-checked']]: isDisabled && checked,
        [styles['radio-disabled']]: isDisabled,
        [styles['radio-inline']]: style === 'inline',
        [styles['radio-no-last-border']]: style === 'no-last-border',
        [styles['radio-label-active']]: checked
    });
    const contentClassName = cx(styles['radio-content'], props.contentClassName, {
        [styles['radio-content-no-left-margin']]: childHideRadio
    });
    return (<label htmlFor={idStr} className={className} onClick={onClickWrapper}>
			<input disabled={isDisabled} type="radio" className={styles['radio-button']} name={name} checked={checked} required={required} readOnly id={idStr}/>
			{!childHideRadio && <div className={styles['radio-indicator']}/>}
			{contentClassName ? (<div className={contentClassName}>{children}</div>) : (<React.Fragment>{children}</React.Fragment>)}
		</label>);
}
Radio.propTypes = {
    allowUnselect: PropTypes.bool,
    checked: PropTypes.bool,
    children: PropTypes.element.isRequired,
    disabled: PropTypes.any,
    hideRadio: PropTypes.bool,
    id: PropTypes.any.isRequired,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    style: PropTypes.oneOf(['inline', 'no-last-border'])
};
export default function RadioList(props) {
    const { children, name, checkedId, style, onChange, required, hideRadio, allowUnselect, contentClassName, containerClassName } = props;
    if (!children)
        return null;
    const listitems = React.Children.map(children, child => {
        if (!child || typeof child === 'string' || typeof child === 'number' || typeof child === 'boolean')
            return null;
        // TODO: The current implementation accepts any child for the list
        // items. This means our API requires users to add props like
        // "disabled" and "checked" to any random HTML element, which breaks TS
        // type-checking on native DOM elements. Hence, support has been added
        // to expose these members as data-* attributes. However, this is not
        // clean. Come up with a wrapper that wraps the actual element that
        // gets shown as the child element, and that wrapper takes the disabled
        // and checked props.
        //
        // The current implementation is especially ugly considering we pass
        // these attributes down to the child component when cloning. So, for
        // example, the "checked" prop for payment option won't work if it was
        // substituted with 'data-checked' instead. To solve this, we are
        // passing the disabled and checked props to the child again. Check
        // code related to the cloning part.
        // Assigning child to tempChild to get rid of ts-lint issues created because of this "{}" type.
        const tempChild = child;
        const disabled = tempChild.props.disabled || tempChild.props['data-disabled'];
        const childKey = tempChild.key ? tempChild.key : genUniqueId();
        // Please use checked on each child instead of checkedId on the collection
        let checked = checkedId === childKey;
        if (typeof checkedId === 'undefined') {
            checked = tempChild.props.checked || tempChild.props['data-checked'];
        }
        // We've to remove className from the child to avoid applying it twice when
        // the child is a non-react component. The class name should be applied
        // only to the <label> component inside Radio component. This is not a
        // problem when the child is a React Component because that gets swallowed
        // when passing it to Radio component.
        //
        // The className value for the new component should be `undefined` so that
        // it effectively removes the props from the rendered DOM markup. If we use
        // `null` or an empty object, the DOM markup will have a className prop (in
        // test snapshots). Although this might not result any visible UI change,
        // it's better not to have it.
        //
        // We need to pass the normalized disabled and checked props to the
        // child even if data-* attributes might've been defined already.
        // TODO: Decide whether the data-{checked,disabled} props need to be
        // passed down after aliasing them. Not doing that would probably
        // confuse if someone depends on those.
        const _a = tempChild.props, { className } = _a, restChildProps = __rest(_a, ["className"]);
        const newProps = Object.assign(Object.assign({}, restChildProps), { className: undefined, disabled, checked }); // eslint-disable-line no-undefined
        const modifiedChild = React.cloneElement(tempChild, newProps);
        /* TODO: consider adding default props instead of defaulting to genUniqueId */
        return (<Radio name={name} checked={checked} required={required} key={childKey} onChange={onChange} style={style} disabled={disabled} id={childKey} hideRadio={hideRadio} allowUnselect={allowUnselect} className={className} contentClassName={contentClassName}>
				{modifiedChild}
			</Radio>);
    });
    return <div className={containerClassName}>{listitems}</div>;
}
RadioList.propTypes = {
    allowUnselect: PropTypes.bool,
    children: PropTypes.arrayOf(PropTypes.element).isRequired,
    hideRadio: PropTypes.bool,
    name: PropTypes.string.isRequired,
    containerClassName: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    style: PropTypes.oneOf(['inline', 'no-last-border']),
    contentClassName: PropTypes.string,
    required: PropTypes.bool
};
